import {Component, computed, ElementRef, inject, OnInit, signal, ViewChild} from '@angular/core';
import {RouterModule} from '@angular/router';
import {CommonModule} from '@angular/common';
import {StyleClassModule} from 'primeng/styleclass';
import {LayoutService} from '@/layout/service/layout.service';
import {AuthService} from '@/core/services/auth.service';
import {UserProfile} from '@/core/models/interface';
import {AppBreadcrumb} from './app.breadcrumb';
import {ButtonModule} from 'primeng/button';
import {RippleModule} from 'primeng/ripple';
import {AvatarModule} from 'primeng/avatar';
import {environment} from '../../../environments/environment';

@Component({
    selector: '[app-topbar]',
    standalone: true,
    imports: [RouterModule, CommonModule, StyleClassModule, AppBreadcrumb, ButtonModule, RippleModule, AvatarModule],
    template: `
        <div class="layout-topbar">
            <div class="topbar-left">
                <a tabindex="0" #menubutton type="button" class="menu-button" (click)="onMenuButtonClick()">
                    <i class="pi pi-chevron-left"></i>
                </a>
                <img class="horizontal-logo" src="/layout/images/logo-white.svg" alt="logo"/>
                <span class="topbar-separator"></span>
                <div app-breadcrumb></div>
                <a routerLink="/">
                    <img class="mobile-logo" src="/layout/images/logo-{{ isDarkTheme() ? 'white' : 'dark' }}.svg"
                         alt="logo"/>
                </a>
            </div>

            <div class="topbar-right">
                <ul class="topbar-menu">
                    <li class="right-sidebar-item">
                        <button class="app-config-button" (click)="onConfigButtonClick()"><i class="pi pi-cog"></i>
                        </button>
                    </li>
                    <li class="profile-item static sm:relative">
                        <a class="right-sidebar-button relative z-50" pStyleClass="@next" enterFromClass="hidden"
                           enterActiveClass="animate-scalein" leaveActiveClass="animate-fadeout" leaveToClass="hidden"
                           [hideOnOutsideClick]="true">
                            <p-avatar styleClass="w-10! h-10!">
                                <img src="/layout/images/profile.png"/>
                            </p-avatar>
                        </a>
                        <div
                            class="list-none p-2 m-0 rounded-2xl border border-surface overflow-hidden absolute bg-surface-0 dark:bg-surface-900 hidden origin-top w-72 mt-2 right-0 z-999 top-auto shadow-[0px_56px_16px_0px_rgba(0,0,0,0.00),0px_36px_14px_0px_rgba(0,0,0,0.01),0px_20px_12px_0px_rgba(0,0,0,0.02),0px_9px_9px_0px_rgba(0,0,0,0.03),0px_2px_5px_0px_rgba(0,0,0,0.04)]"
                        >
                            <!-- Identidad del usuario autenticado: correo + ID de Cognito (copiable). -->
                            <div class="px-2.5 py-2 border-b border-surface mb-1">
                                <span class="block text-sm font-medium text-surface-950 dark:text-surface-0 break-all">
                                    {{ profile()?.email || 'Sin correo' }}
                                </span>
                                <div *ngIf="profile()?.userId" class="flex items-center gap-1 mt-1">
                                    <span class="text-xs text-surface-500 dark:text-surface-400 font-mono truncate"
                                          [title]="profile()!.userId">
                                        {{ profile()!.userId }}
                                    </span>
                                    <button type="button" pRipple
                                            class="shrink-0 w-6 h-6 flex items-center justify-center rounded-md hover:bg-emphasis transition-colors duration-150 cursor-pointer"
                                            [title]="userIdCopied() ? 'ID copiado' : 'Copiar ID'"
                                            (click)="copyUserId($event)">
                                        <i class="text-xs"
                                           [ngClass]="userIdCopied() ? 'pi pi-check text-green-500' : 'pi pi-copy text-surface-500 dark:text-surface-400'"></i>
                                    </button>
                                </div>
                            </div>
                            <ul class="flex flex-col gap-1">
                                <li>
                                    <a routerLink="/profile"
                                       class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer">
                                        <i class="pi pi-user"></i>
                                        <span>Perfil</span>
                                    </a>
                                </li>
                                <li>
                                    <a (click)="onConfigButtonClick()"
                                       class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer">
                                        <i class="pi pi-cog"></i>
                                        <span>Configuración</span>
                                    </a>
                                </li>
                                <li>
                                    <a (click)="logout()" class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer">
                                        <i class="pi pi-power-off"></i>
                                        <span>Cerrar sesión</span>
                                    </a>
                                </li>
                            </ul>
                            <div class="border-t border-surface mt-1 px-2.5 pt-2 pb-1">
                                <span class="text-sm text-surface-500 dark:text-surface-400">Versión {{ appVersion }}</span>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>`
})
export class AppTopbar implements OnInit {
    layoutService = inject(LayoutService);
    private authService = inject(AuthService);

    isDarkTheme = computed(() => this.layoutService.isDarkTheme());

    appVersion = environment.appVersion;

    @ViewChild('menubutton') menuButton!: ElementRef;

    /** Correo e ID que se muestran arriba del menú de perfil. */
    profile = signal<UserProfile | null>(null);

    /** Feedback del botón de copiar: cambia el icono a palomita ~1.5s. */
    userIdCopied = signal(false);

    async ngOnInit(): Promise<void> {
        try {
            this.profile.set(await this.authService.getUserProfile());
        } catch (error) {
            console.error('AppTopbar: no se pudo cargar el perfil del usuario', error);
        }
    }

    async copyUserId(event: Event): Promise<void> {
        // Sin esto el click cierra el overlay de `pStyleClass` antes de que se vea la palomita.
        event.stopPropagation();
        const userId = this.profile()?.userId;
        if (!userId) {
            return;
        }
        try {
            await navigator.clipboard.writeText(userId);
            this.userIdCopied.set(true);
            setTimeout(() => this.userIdCopied.set(false), 1500);
        } catch (error) {
            console.error('AppTopbar: no se pudo copiar el ID', error);
        }
    }

    onMenuButtonClick() {
        this.layoutService.onMenuToggle();
    }

    onConfigButtonClick() {
        this.layoutService.showConfigSidebar();
    }

    toggleSearchBar() {
        this.layoutService.layoutState.update((value) => ({...value, searchBarActive: !value.searchBarActive}));
    }

    logout() {
        this.authService.logout();
    }
}
